import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { FileWithPath } from "@mantine/dropzone";
import axios from "axios";
import {
  CreateFileDocument,
  DeleteFileDocument,
  GetFilesOfOrderDocument,
  GET_FILE_DOWNLOAD_URL,
  GET_FILE_UPLOAD_URL,
} from "../gql/files";
import { FileType } from "../types/enums";
import imageCompression from "browser-image-compression";

export const useFiles = (orderId: string) => {
  const { data: filesOfOrder, refetch: refetchFilesOfOrder } = useQuery(
    GetFilesOfOrderDocument,
    {
      variables: {
        where: {
          orderId: { equals: orderId },
        },
      },
      fetchPolicy: "network-only",
    }
  );
  const [createFile] = useMutation(CreateFileDocument, {
    refetchQueries: [GetFilesOfOrderDocument],
  });
  const [deleteFile] = useMutation(DeleteFileDocument, {
    refetchQueries: [GetFilesOfOrderDocument],
  });
  const [getPresignedDownload] = useLazyQuery(GET_FILE_DOWNLOAD_URL);
  const [getPresignedPost] = useLazyQuery(GET_FILE_UPLOAD_URL, {
    fetchPolicy: "network-only",
  });

  const getPresignedDownloadUrl = async (key: string) => {
    const res = await getPresignedDownload({
      variables: {
        key: key,
      },
    });
    window.open(res?.data?.getPresignedFileDownloadUrl, "_blank");
  };

  const getFileUrl = async (key: string) => {
    const res = await getPresignedDownload({
      variables: {
        key: key,
      },
    });
    return res?.data?.getPresignedFileDownloadUrl;
  };

  const deleteOneFile = async (id: string, key: string) => {
    await deleteFile({
      variables: {
        key: key,
        where: {
          id: id,
        },
      },
    });
  };

  const imageToCompress = async (file: FileWithPath) => {
    const options = {
      maxSizeMB: 1,
      maxWidthOrHeight: 1920,
      useWebWorker: true,
    };
    const compressedBlob = await imageCompression(file, options);
    return new File([compressedBlob], "compressed-file-" + file.name);
  };

  // functions to upload to s3
  const uploadFileToS3 = async (
    files: FileWithPath[] | File[],
    fileType?: FileType
  ): Promise<any[] | null> => {
    if (!files) return null;
    const filesWithKey: any[] = await Promise.all(
      files.map(async file => {
        let presignedPostCompressed: any = undefined;
        if (
          fileType === FileType.CHAT_FILE &&
          file.type &&
          file.type.split("/")[0] === "image"
        ) {
          const compressedImage = await imageToCompress(file);
          const getPresignedPostDataCompressed = await getPresignedPost({
            variables: {
              fileName: compressedImage.name,
            },
          });
          presignedPostCompressed = JSON.parse(
            getPresignedPostDataCompressed?.data?.getPresignedFileUploadUrl ??
              ""
          );
          const uploadFormDataCompressed = new FormData();
          Object.keys(presignedPostCompressed.fields).forEach(key => {
            uploadFormDataCompressed.append(
              key,
              presignedPostCompressed.fields[key]
            );
          });
          uploadFormDataCompressed.append("file", compressedImage);
          const resCompressed = await axios({
            method: "POST",
            url: presignedPostCompressed.url,
            data: uploadFormDataCompressed,
            headers: { "Content-Type": "multipart/form-data" },
          });
        }
        const getPresignedPostData = await getPresignedPost({
          variables: {
            fileName: file.name,
          },
        });
        const presignedPost = JSON.parse(
          getPresignedPostData?.data?.getPresignedFileUploadUrl ?? ""
        );
        const uploadFormData = new FormData();
        Object.keys(presignedPost.fields).forEach(key => {
          uploadFormData.append(key, presignedPost.fields[key]);
        });
        uploadFormData.append("file", file);
        const res = await axios({
          method: "POST",
          url: presignedPost.url,
          data: uploadFormData,
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (res.status === 201 && presignedPost.fields.key) {
          // Add to the filesWithKey array
          const newFileData = {
            fileType: FileType.CHAT_FILE,
            key: presignedPost.fields.key ?? null,
            keyCompressed:
              presignedPostCompressed !== undefined
                ? (presignedPostCompressed.fields.key ?? null)
                : null,
            order: {
              connect: {
                id: orderId,
              },
            },
          };

          if (!fileType || fileType !== FileType.CHAT_FILE) {
            await createFile({
              variables: {
                args: {
                  key: presignedPost.fields.key ?? null,
                  order: {
                    connect: {
                      id: orderId,
                    },
                  },
                  fileType:
                    fileType !== FileType.ORDER_FILE ? fileType : "OrderFile",
                },
              },
            });
          }

          return newFileData;
        }

        return null;
      })
    );

    return filesWithKey.filter(file => file !== null); // Filter out null values if any
  };

  const dataURLtoFile = (dataurl: string, filename: string): File => {
    const [meta, base64Data] = dataurl.split(",");
    const mimeMatch = meta.match(/:(.*?);/);

    if (!mimeMatch) {
      throw new Error("Invalid data URL");
    }

    const mime = mimeMatch[1];
    const binaryString = atob(base64Data);
    const length = binaryString.length;
    const u8arr = new Uint8Array(length);

    for (let i = 0; i < length; i++) {
      u8arr[i] = binaryString.charCodeAt(i);
    }
    return new File([u8arr], filename, { type: mime });
  };

  return {
    getPresignedDownloadUrl,
    deleteOneFile,
    uploadFileToS3,
    filesOfOrder,
    dataURLtoFile,
    getFileUrl,
    refetchFilesOfOrder,
  };
};
